import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { api } from '../api';
import ActivateSellerModal from '../components/ActivateSellerModal';

// Custom Dropdown Component for Action Column
const SellerAction = ({ row, onInfo, onActivate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const toggleDropdown = (e) => {
    e.stopPropagation(); // Prevent row click
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        // Align the right edge of the menu with the right edge of the button
        // We'll use CSS transform to shift it left, so left should be rect.right
        left: rect.right + window.scrollX,
      });
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close if click is NOT in button AND NOT in menu
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) setIsOpen(false); // Close on scroll to avoid detached menu
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const menu = (
    <div
      ref={menuRef}
      className="dropdown-menu show"
      style={{
        position: 'absolute',
        top: menuPosition.top,
        left: menuPosition.left,
        zIndex: 9999,
        display: 'block',
        minWidth: '150px',
        transform: 'translateX(-100%)', // Shift left to align right edge
      }}
    >
      <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); setIsOpen(false); onInfo(row); }} style={{ color: '#000', fontWeight: '500' }}>
        Info
      </button>
      <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); setIsOpen(false); onActivate(row); }} style={{ color: '#000', fontWeight: '500' }}>
        Activate Seller
      </button>
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        className="btn btn-link text-secondary p-0"
        type="button"
        onClick={toggleDropdown}
        style={{ textDecoration: 'none' }}
      >
        <i className="mdi mdi-dots-vertical" style={{ fontSize: '20px', color: '#000' }}></i>
      </button>
      {isOpen && createPortal(menu, document.body)}
    </>
  );
};

function Sellers() {
  const navigate = useNavigate();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);

  // Modal state
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [activating, setActivating] = useState(false);

  const fetchSellers = async (pageNum = page, limitNum = perPage) => {
    setLoading(true);
    setError(null);
    const res = await api.getSellers(pageNum, limitNum);
    if (!res) {
      setError('Failed to load sellers');
      setSellers([]);
      setTotalRows(0);
    } else {
      setSellers(res.sellers || []);
      setTotalRows(res.total || 0);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSellers(page, perPage);
    // eslint-disable-next-line
  }, [page, perPage]);

  const handlePageChange = pageNum => setPage(pageNum);
  const handlePerRowsChange = async (newPerPage, pageNum) => {
    setPerPage(newPerPage);
    setPage(pageNum);
  };

  const handleActivateClick = (seller) => {
    setSelectedSeller(seller);
    setShowActivateModal(true);
  };

  const handleActivateSubmit = async (data) => {
    if (!selectedSeller) return;

    setActivating(true);
    const res = await api.activateSeller(selectedSeller.email, data);
    setActivating(false);

    if (res.ok) {
      toast.success('Seller activated successfully!');
      setShowActivateModal(false);
      fetchSellers(); // Refresh list
    } else {
      toast.error(`Failed to activate seller: ${res.message || 'Unknown error'}`);
    }
  };

  const columns = [
    { name: 'Profile', selector: row => <img className="vendor-thumb" src={row.imageUrl || '/assets/img/vendor/u9.jpg'} alt="seller profile" />, width: '80px' },
    { name: 'Name', selector: row => row.name || '-', sortable: true },
    { name: 'Email', selector: row => row.email || '-', sortable: true },
    { name: 'Phone', selector: row => row.phone || '-', sortable: true },
    { name: 'Status', selector: row => <span className={`ec-status ${row.status === 'ACTIVE' ? 'ec-active' : row.status === 'PENDING' ? 'ec-pending' : 'ec-inactive'}`}>{row.status || '-'}</span>, sortable: true },
    { name: 'Verification', selector: row => row.verificationStatus || '-', sortable: true },
    { name: 'Join On', selector: row => row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-', sortable: true },
    {
      name: 'Action',
      cell: row => (
        <SellerAction
          row={row}
          onInfo={(r) => navigate(`/sellers/${r.id}`, { state: { seller: r } })}
          onActivate={handleActivateClick}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '100px',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Sellers | Owena Admin Dashboard</title>
      </Helmet>
      <div className="ec-content-wrapper">
        <div className="content">
          <div className="breadcrumb-wrapper breadcrumb-contacts">
            <div>
              <h1>Sellers</h1>
              <p className="breadcrumbs"><span><a href="#" onClick={(e) => e.preventDefault()}>Home</a></span>
                <span><i className="mdi mdi-chevron-right"></i></span>Sellers</p>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="ec-vendor-list card card-default">
                <div className="card-body">
                  {error && <div className="alert alert-danger">{error}</div>}
                  <DataTable
                    columns={columns}
                    data={sellers}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    highlightOnHover
                    responsive
                    noHeader
                  />
                </div>
              </div>
            </div>
          </div>

          <ActivateSellerModal
            show={showActivateModal}
            onClose={() => setShowActivateModal(false)}
            onSubmit={handleActivateSubmit}
            sellerEmail={selectedSeller?.email}
            isLoading={activating}
          />
        </div>
      </div>
    </>
  );
}

export default Sellers;
